define('chatInboxDetailView',[
  'module',
  'app',
  'marionette',
  'backbone',
  'underscore',
  'jquery',
  'settings',
  'notificationCenter',
  'entities/chat_inbox'
], function (Module, App, Marionette, Backbone, _, $, Settings, NotificationCenter) {
  'use strict';

  function escapeHtml(s) {
    var str = (s === null || s === undefined) ? '' : String(s);
    return str
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/\n/g, '<br/>');
  }

  // Attribute-safe escape — like escapeHtml but does NOT turn newlines into
  // <br/> (used for href/src values).
  function escapeAttr(s) {
    var str = (s === null || s === undefined) ? '' : String(s);
    return str
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function formatSize(bytes) {
    if (bytes === null || bytes === undefined || bytes === '') { return ''; }
    var n = Number(bytes);
    if (isNaN(n)) { return ''; }
    if (n < 1024) { return n + ' B'; }
    if (n < 1024 * 1024) { return Math.round(n / 1024) + ' KB'; }
    return (n / (1024 * 1024)).toFixed(1) + ' MB';
  }

  // Render a message's attachment, if any: an inline thumbnail for images,
  // otherwise a download link with the filename and size. Returns '' for a
  // plain text message.
  function renderAttachment(msg) {
    if (!msg.fileUrl) { return ''; }
    var name = msg.fileName || 'file';
    var isImage = (msg.fileContentType || '').indexOf('image/') === 0;
    if (isImage) {
      return '<div class="attachment image">' +
        '<a href="' + escapeAttr(msg.fileUrl) + '" target="_blank" rel="noopener">' +
        '<img src="' + escapeAttr(msg.fileUrl) + '" alt="' + escapeHtml(name) + '"/></a>' +
        '</div>';
    }
    var size = formatSize(msg.fileSize);
    return '<div class="attachment file">' +
      '<a href="' + escapeAttr(msg.fileUrl) + '" target="_blank" rel="noopener" download>' +
      '📎 ' + escapeHtml(name) + '</a>' +
      (size ? ' <span class="size">(' + escapeHtml(size) + ')</span>' : '') +
      '</div>';
  }

  function statusLabel(s) {
    return (s || '').replace(/_/g, ' ').toLowerCase();
  }

  function relativeTime(date) {
    if (!date) { return ''; }
    var diff = Date.now() - date.getTime();
    var min = Math.round(diff / 60000);
    if (min < 1)  { return 'just now'; }
    if (min < 60) { return min + ' min ago'; }
    var hr = Math.round(min / 60);
    if (hr < 24)  { return hr + ' h ago'; }
    return Math.round(hr / 24) + ' d ago';
  }

  // Render one bubble. Customer messages are right-aligned on the agent's
  // screen (mirroring how the visitor sees their own messages on the right
  // in most chat widgets); agent messages are left-aligned. AI and SYSTEM
  // messages render in muted styles so they don't compete visually.
  function renderMessage(msg) {
    var sender = msg.sender || 'SYSTEM';
    var side = sender === 'CUSTOMER' ? 'incoming' : 'outgoing';
    var senderLabel;
    switch (sender) {
      case 'CUSTOMER': senderLabel = 'Visitor'; break;
      case 'AGENT':    senderLabel = msg.userpidName || 'Agent'; break;
      case 'AI':       senderLabel = 'AI assistant'; break;
      case 'SYSTEM':   senderLabel = 'System'; break;
      default:         senderLabel = sender;
    }
    var when = msg.sentAt ? new Date(msg.sentAt) : null;
    var pending = sender === 'AGENT' && !msg.deliveredToCustomerAt;
    return '' +
      '<li class="msg msg-' + side + ' sender-' + sender + (pending ? ' pending' : '') + '">' +
      '  <div class="bubble">' +
      '    <div class="meta"><span class="who">' + escapeHtml(senderLabel) + '</span>' +
      (when ? ' <span class="when" title="' + escapeHtml(when.toLocaleString()) + '">' + escapeHtml(relativeTime(when)) + '</span>' : '') +
      (pending ? ' <span class="delivery">sending…</span>' : '') +
      '    </div>' +
      (msg.text ? '    <div class="text">' + escapeHtml(msg.text) + '</div>' : '') +
      renderAttachment(msg) +
      '  </div>' +
      '</li>';
  }

  Module.exports = Marionette.LayoutView.extend({
    className: 'chat-inbox-detail-view',
    template: _.template(
      '<div class="detail-header">' +
      '  <div class="back"><a href="#chat-inbox">&larr; Chat Inbox</a></div>' +
      '  <div class="hdr-row">' +
      '    <div class="hdr-main">' +
      '      <div class="title">' +
      '        <span class="status-chip s-<%= status %>"><%= statusLabel %></span>' +
      '        <span class="presence-dot <%= online ? \'on\' : \'off\' %>" ' +
      '              title="<%= online ? \'Customer connected\' : \'Customer offline\' %>"></span>' +
      '        <span class="who"><%= visitorName %></span>' +
      '        <% if (senderEmail) { %><span class="email">&lt;<%= senderEmail %>&gt;</span><% } %>' +
      '      </div>' +
      '      <div class="sub-line">' +
      '        <% if (locale) { %><span class="locale"><%= locale.toUpperCase() %></span><% } %>' +
      '        <% if (startedAt) { %><span class="when">started <%= new Date(startedAt).toLocaleString() %></span><% } %>' +
      '        <% if (pageUrl) { %><span class="page">on <a href="<%= pageUrl %>" target="_blank" rel="noopener" title="<%= pageUrl %>"><%= pageUrl %></a></span><% } %>' +
      '        <% if (ipAddress) { %><span class="ip">IP <%- ipAddress %><% if (ipBanned) { %> <span class="ip-banned">· banned</span><% } %></span><% } %>' +
      '      </div>' +
      '    </div>' +
      '    <div class="hdr-actions">' +
      '      <button class="btn btn-claim" <%= canClaim ? "" : "disabled" %>>Assign to me</button>' +
      '      <button class="btn btn-release" <%= canRelease ? "" : "disabled" %>>Release</button>' +
      '      <button class="btn btn-resolve" <%= canResolve ? "" : "disabled" %>>Resolve</button>' +
      '      <% if (canReopen) { %><button class="btn btn-reopen"><%= isSpam ? "Not spam" : "Reopen" %></button><% } %>' +
      '      <button class="btn btn-file-upload <%= fileUploadEnabled ? \'on\' : \'\' %>" <%= canToggleFileUpload ? "" : "disabled" %> title="<%- fileToggleTitle %>"><%- fileToggleLabel %></button>' +
      '      <button class="btn <%= ipBanned ? \'btn-unban\' : \'btn-ban\' %>" <%= canBan ? "" : "disabled" %> title="<%- banTitle %>"><%- banLabel %></button>' +
      '      <button class="btn btn-delete" title="Delete this chat permanently">Delete</button>' +
      '    </div>' +
      '  </div>' +
      '  <div class="hdr-assignee">' +
      '    <span class="team-tag"><%= assignedGroupName || "No team" %></span>' +
      '    <span class="assignee-tag <%= assignedUserpidName ? "" : "unassigned" %>">' +
      '      <%= assignedUserpidName || "Unclaimed" %>' +
      '    </span>' +
      '  </div>' +
      '</div>' +

      '<div class="thread-panel">' +
      '  <ul class="msg-list"></ul>' +
      '</div>' +

      '<div class="reply-bar">' +
      '  <textarea class="reply-input" rows="2" placeholder="<%= replyPlaceholder %>" <%= canReply ? "" : "disabled" %>></textarea>' +
      '  <button class="btn btn-send" <%= canReply ? "" : "disabled" %>>Send</button>' +
      '</div>'
    ),

    events: {
      'click .btn-claim':   'onClaim',
      'click .btn-release': 'onRelease',
      'click .btn-resolve': 'onResolve',
      'click .btn-reopen':  'onReopen',
      'click .btn-ban':     'onBan',
      'click .btn-unban':   'onUnban',
      'click .btn-file-upload': 'onToggleFileUpload',
      'click .btn-delete':  'onDelete',
      'click .btn-send':    'onSend',
      'keydown .reply-input': 'onReplyKeydown'
    },

    initialize: function (options) {
      this.secId = options.secId;
      this.session = null;
      this.messages = [];
    },

    serializeData: function () {
      var s = this.session || {};
      var mySecId = (Settings.get('currentUser') && Settings.get('currentUser').secId) || null;
      var assigned = !!s.assignedUserpidSecId;
      var assignedToMe = assigned && s.assignedUserpidSecId === mySecId;
      var resolved = s.status === 'RESOLVED' || s.status === 'ABANDONED';
      // Auto-quarantined junk. Treated as closed for the action buttons (no
      // resolve/reply) but recoverable via a "Not spam" button that reuses
      // the reopen path to push a mis-flagged visitor back into the queue.
      var isSpam = s.status === 'SPAM';

      return {
        status: s.status || 'WAITING_HUMAN',
        statusLabel: statusLabel(s.status),
        isSpam: isSpam,
        online: !!s.online,
        visitorName: s.customerName || s.senderName || 'Anonymous visitor',
        senderEmail: s.senderEmail || '',
        locale: s.locale || '',
        startedAt: s.startedAt || null,
        pageUrl: s.pageUrl || '',
        assignedGroupName: s.assignedGroupName || '',
        assignedUserpidName: s.assignedUserpidName || '',
        // Anyone can assign the chat to themselves — even one already taken
        // by a colleague — as long as it isn't already theirs.
        canClaim:   !assignedToMe && !resolved && !isSpam,
        // Release stays limited to the current assignee so you don't drop a
        // colleague's chat out from under them; resolve is open to anyone on
        // an active chat.
        canRelease: assignedToMe && !resolved && !isSpam,
        canResolve: !resolved && !isSpam,
        // Reopen is the inverse of resolve: offered on a closed chat, and on
        // a quarantined (SPAM) one as the "Not spam" recovery. Either way it
        // returns the chat to the group's unclaimed queue (WAITING_HUMAN).
        canReopen: resolved || isSpam,
        // Reply implicitly claims unclaimed chats — so it's allowed whenever
        // the session isn't already taken by someone else (or resolved/spam;
        // recover it with "Not spam" first).
        canReply:   !resolved && !isSpam && (!assigned || assignedToMe),
        replyPlaceholder: isSpam ?
          'Quarantined as spam — click “Not spam” to reply' :
          (resolved ?
          'Conversation resolved' :
          (assigned && !assignedToMe ?
              'Claimed by ' + (s.assignedUserpidName || 'another agent') :
              'Type a reply…')),
        // Ban / Unban toggle. Only offered when the session recorded the
        // visitor's IP (older sessions, or an unresolved IP, have none — the
        // button is disabled and the title explains why). The label and
        // colour flip on `ipBanned`, which the detail endpoint resolves.
        // File-upload permission toggle. Off by default; an agent flips it on
        // to let the visitor attach files (the widget then reveals its attach
        // button). Allowed whenever the chat is active.
        fileUploadEnabled:   !!s.fileUploadEnabled,
        canToggleFileUpload: !resolved,
        fileToggleLabel:     s.fileUploadEnabled ? 'Files: on' : 'Allow files',
        fileToggleTitle:     resolved ?
          'Re-open the chat to change file-upload permission' :
          (s.fileUploadEnabled ?
              'Stop the visitor from attaching files' :
              'Let the visitor attach files in this chat'),
        ipAddress: s.ipAddress || '',
        ipBanned:  !!s.ipBanned,
        canBan:    !!s.ipAddress,
        banLabel:  s.ipBanned ? 'Unban' : 'Ban',
        banTitle:  !s.ipAddress ?
          'No IP address was recorded for this visitor' :
          (s.ipBanned ?
              'Lift the chat ban on ' + s.ipAddress :
              'Ban ' + s.ipAddress + ' from the chat')
      };
    },

    onBeforeShow: function () {
      this.load();
      this.subscribeToNotifications();
      this.startPresencePolling();
    },

    /**
     * Poll the visitor's connection status. Presence isn't a chat event
     * (no message fires when a visitor just closes their tab), so it can't
     * piggyback on the WS notification fabric — a light 15s poll keeps the
     * header dot fresh while the agent is in the conversation. The interval
     * also smooths over the brief disconnect/reconnect of a webshop page
     * navigation.
     */
    startPresencePolling: function () {
      var self = this;
      var poll = function () {
        if (self.isDestroyed) { return; }
        App.request('chatInbox:presence', self.secId).done(function (online) {
          if (self.isDestroyed) { return; }
          self.applyPresence(online);
        });
      };
      poll();
      this.presenceTimer = setInterval(poll, 15000);
    },

    // Patch the header dot in place — avoids a full re-render that would
    // drop focus on the reply textarea.
    applyPresence: function (online) {
      if (this.session) { this.session.online = online; }
      var $dot = this.$('.presence-dot');
      if ($dot.length) {
        $dot.toggleClass('on', !!online).toggleClass('off', !online);
        $dot.attr('title', online ? 'Customer connected' : 'Customer offline');
      }
    },

    onDestroy: function () {
      if (this.presenceTimer) {
        clearInterval(this.presenceTimer);
        this.presenceTimer = null;
      }
    },

    /**
     * Listen for chat events from the WS notification fabric. We filter by
     * the entitySecId on the inner content payload — every chat-event the
     * backend publishes carries the session's secId there — so we only
     * react to our own session's events and ignore unrelated noise.
     */
    subscribeToNotifications: function () {
      var self = this;
      var nc = NotificationCenter.getInstance();

      function isOurs(notification) {
        var inner = notification && notification.content && notification.content.content;
        return inner && inner.entitySecId === self.secId;
      }

      nc.addListener(this, NotificationCenter.events.CHAT_MESSAGE_ADDED, function (n) {
        if (!isOurs(n)) { return; }
        // The message payload is delivered as `data` on the inner content,
        // but to keep things simple (and to pick up server-side derived
        // fields like deliveredToCustomerAt) we just refetch the thread.
        self.loadMessages();
      });
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_CLAIMED, function (n) {
        if (!isOurs(n)) { return; }
        self.load();
      });
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_RELEASED, function (n) {
        if (!isOurs(n)) { return; }
        self.load();
      });
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_RESOLVED, function (n) {
        if (!isOurs(n)) { return; }
        self.load();
      });
      // Visitor navigated to a new page. Patch the header in place so the
      // agent sees the current page in real-time without losing focus on
      // the reply textarea (a full self.load() / self.render() would).
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_PAGE_CHANGED, function (n) {
        if (self.isDestroyed || !isOurs(n)) { return; }
        var inner = n.content.content;
        var data = inner.data || {};
        var newUrl = data.pageUrl || inner.text || '';
        if (!newUrl) { return; }
        if (self.session) { self.session.pageUrl = newUrl; }
        var $page = self.$('.page');
        if ($page.length) {
          // Underscore's template auto-escapes; do the same here for the
          // in-place update since pageUrl is visitor-controlled input.
          $page.html('on <a href="' + _.escape(newUrl) + '" target="_blank" rel="noopener" title="' + _.escape(newUrl) + '">' + _.escape(newUrl) + '</a>');
        } else {
          // First-time page (header had no .page span yet) — pull in a
          // full re-render to insert it.
          self.render();
          self.renderMessages();
        }
      });
      // Another agent toggled the file-upload permission. Patch the button in
      // place (don't full-render — keep the reply textarea focus).
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_FILE_UPLOAD_CHANGED, function (n) {
        if (self.isDestroyed || !isOurs(n)) { return; }
        var data = (n.content && n.content.content && n.content.content.data) || {};
        var enabled = !!data.fileUploadEnabled;
        if (self.session) { self.session.fileUploadEnabled = enabled; }
        var $btn = self.$('.btn-file-upload');
        if ($btn.length) {
          $btn.toggleClass('on', enabled);
          $btn.text(enabled ? 'Files: on' : 'Allow files');
          $btn.attr('title', enabled ?
            'Stop the visitor from attaching files' :
            'Let the visitor attach files in this chat');
        }
      });
    },

    load: function () {
      var self = this;
      // Every .done()/.fail() below guards on isDestroyed: the request can
      // resolve after the user navigated away, and self.render() on a
      // destroyed Marionette view throws ViewDestroyedError.
      App.request('chatInbox:get', this.secId).done(function (data) {
        if (self.isDestroyed) { return; }
        var d = data || {};
        self.session = d.session || null;
        self.messages = d.messages || [];
        // render() rebuilds the whole DOM, including the reply textarea —
        // capture its focus/value/caret first and restore after, so the
        // agent's place isn't lost (notably right after pressing Enter to
        // send: onSend re-focuses, then this render would otherwise drop it).
        var replyState = self.captureReplyState();
        self.render();
        self.renderMessages();
        self.restoreReplyState(replyState);
        self.scrollToBottom();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        self.$el.html('<div class="error">Could not load chat session.</div>');
      });
    },

    // Snapshot the reply input before a full re-render.
    captureReplyState: function () {
      var $input = this.$('.reply-input');
      if (!$input.length) { return null; }
      var inputEl = $input[0];
      return {
        focused: document.activeElement === inputEl,
        value: $input.val(),
        selStart: inputEl.selectionStart,
        selEnd: inputEl.selectionEnd
      };
    },

    // Re-apply the snapshot to the freshly-rendered reply input.
    restoreReplyState: function (state) {
      if (!state) { return; }
      var $input = this.$('.reply-input');
      if (!$input.length) { return; }
      var inputEl = $input[0];
      // Only re-apply a non-empty draft — after a send onSend cleared the
      // field and we want it to stay cleared.
      if (state.value) { $input.val(state.value); }
      if (state.focused && !$input.prop('disabled')) {
        $input.focus();
        try {
          inputEl.setSelectionRange(state.selStart, state.selEnd);
        } catch (e) { /* setSelectionRange unsupported in this state — ignore */ }
      }
    },

    // Lighter refresh used when only the messages array changed (WS-driven
    // CHAT_MESSAGE_ADDED). Avoids re-rendering the header and losing focus
    // on the reply textarea.
    loadMessages: function () {
      var self = this;
      App.request('chatInbox:get', this.secId).done(function (data) {
        if (self.isDestroyed) { return; }
        var d = data || {};
        self.messages = d.messages || [];
        self.renderMessages();
        self.scrollToBottom();
      });
    },

    renderMessages: function () {
      var $list = this.$('.msg-list');
      if (!$list.length) { return; }
      var html = _.map(this.messages, renderMessage).join('');
      $list.html(html || '<li class="empty">No messages yet.</li>');
    },

    scrollToBottom: function () {
      var $panel = this.$('.thread-panel');
      if ($panel.length) { $panel.scrollTop($panel[0].scrollHeight); }
    },

    onClaim: function () {
      var self = this;
      App.request('chatInbox:claim', this.secId).done(function () {
        if (self.isDestroyed) { return; }
        self.load();
      }).fail(function (err) {
        if (self.isDestroyed) { return; }
        if (err && err.raceLost && err.current) {
          self.session = err.current;
          self.render();
          self.renderMessages();
          window.alert('Already claimed by ' + (err.current.assignedUserpidName || 'another agent'));
        }
      });
    },

    onRelease: function () {
      var self = this;
      App.request('chatInbox:release', this.secId).done(function () {
        if (self.isDestroyed) { return; }
        self.load();
      });
    },

    onResolve: function () {
      if (!window.confirm('Mark this chat as resolved?')) { return; }
      var self = this;
      App.request('chatInbox:resolve', this.secId).done(function () {
        if (self.isDestroyed) { return; }
        self.load();
      });
    },

    onReopen: function () {
      if (!window.confirm('Re-open this chat? It returns to the unclaimed queue.')) { return; }
      var self = this;
      App.request('chatInbox:reopen', this.secId).done(function () {
        if (self.isDestroyed) { return; }
        self.load();
      });
    },

    /**
     * Ban the visitor's IP from the chat. Permanent ban: the backend drops
     * their open socket immediately and refuses any reconnect (HTTP 403).
     * Reloads the detail so the action flips to "Unban".
     */
    onBan: function () {
      var ip = this.session && this.session.ipAddress;
      if (!ip) {
        window.alert('No IP address was recorded for this visitor, so they can\'t be banned.');
        return;
      }
      var reason = window.prompt(
        'Ban this visitor from the chat?\n\nIP address: ' + ip + '\n\n' +
        'Their chat is disconnected immediately and they won\'t be able to reopen it.\n' +
        'Enter a reason (optional), then OK to confirm — or Cancel to abort:', '');
      if (reason === null) { return; }
      var self = this;
      App.request('chatInbox:ban', ip, reason).done(function () {
        if (self.isDestroyed) { return; }
        self.load();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        window.alert('Could not ban the visitor. Please try again.');
      });
    },

    /**
     * Lift the ban on the visitor's IP. Reloads the detail so the action
     * flips back to "Ban".
     */
    onUnban: function () {
      var ip = this.session && this.session.ipAddress;
      if (!ip) { return; }
      if (!window.confirm('Lift the chat ban on ' + ip + '?')) { return; }
      var self = this;
      App.request('chatInbox:unban', ip).done(function () {
        if (self.isDestroyed) { return; }
        self.load();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        window.alert('Could not lift the ban. Please try again.');
      });
    },

    /**
     * Toggle the visitor's permission to attach files. The backend pushes the
     * new state to the widget over its WebSocket, so the visitor's attach
     * button appears/disappears in real time. Reloads the detail so the button
     * label flips.
     */
    onToggleFileUpload: function () {
      var self = this;
      var enable = !(this.session && this.session.fileUploadEnabled);
      App.request('chatInbox:set-file-upload-enabled', this.secId, enable).done(function () {
        if (self.isDestroyed) { return; }
        self.load();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        window.alert('Could not change the file-upload setting.');
      });
    },

    /**
     * Permanently delete the chat. Removes the session, its messages, activity
     * log and any uploaded files on the backend; the visitor's open widget
     * socket is dropped server-side. Navigates back to the (empty) list — the
     * CHAT_SESSION_DELETED notification refreshes the roster for every agent so
     * the row disappears.
     */
    onDelete: function () {
      if (!window.confirm('Delete this chat permanently?\n\nThis removes the conversation, its messages and any files. It cannot be undone.')) {
        return;
      }
      var self = this;
      App.request('chatInbox:delete', this.secId).done(function () {
        if (self.isDestroyed) { return; }
        App.navigate('chat-inbox', { trigger: true });
      }).fail(function () {
        if (self.isDestroyed) { return; }
        window.alert('Could not delete the chat. Please try again.');
      });
    },

    onSend: function () {
      var self = this;
      var $input = this.$('.reply-input');
      var text = ($input.val() || '').trim();
      if (!text) { return; }
      $input.prop('disabled', true);
      App.request('chatInbox:reply', this.secId, text).done(function () {
        if (self.isDestroyed) { return; }
        $input.val('').prop('disabled', false).focus();
        self.load();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        $input.prop('disabled', false);
        window.alert('Could not send reply.');
      });
    },

    // Enter sends, shift+Enter inserts a newline — same default as Slack/Gmail.
    onReplyKeydown: function (e) {
      if (e.keyCode === 13 && !e.shiftKey) {
        e.preventDefault();
        this.onSend();
      }
    }
  });
});

